import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { CgOptions } from "react-icons/cg";
import {
  GET_CATEGORIES_N_SUB,
  GET_PRODUCT_ORIGIN,
  GET_SINGLE_PRODUCTS,
  GET_STATES,
} from "../../Utils/Queries";
import { ADMIN_EDIT_PRODUCT } from "../../Utils/Mutation";
import {
  buildAdminEditProductVariables,
  logEditProductDebug,
  mergeProductImageUrlsAfterUpload,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_TITLE_MAX_LENGTH,
  uploadNewProductImagesToCloudinary,
  validateEditProductForm,
} from "../../Utils/productEditHelpers";
import { useSnackbarHook } from "../../hooks/useSnackbarHook";
import { FiSave, FiTrash2 } from "react-icons/fi";
import StatusChip from "../Common/StatusChip";
import {
  IoAddOutline,
  IoImagesOutline,
  IoPricetagsOutline,
} from "react-icons/io5";
import { GoUpload } from "react-icons/go";
import Loader from "../Common/Loader";
import CustomInput from "../Common/CustomInput";
import MaSearchableSelect from "../Common/ma-dropdown";
import {
  IoIosCheckmarkCircleOutline,
  IoIosInformationCircleOutline,
} from "react-icons/io";
import TagInput from "../Common/tag-input";

const IMAGE_LIMIT_BYTES = 5 * 1024 * 1024;

const EditProduct = () => {
  const { id } = useParams();
  const [imageItems, setImageItems] = useState([]);
  const createdImageUrlsRef = useRef([]);
  const [productDetails, setProductDetails] = useState({
    category: null,
    title: "",
    description: "",
    state: null,
    city: null,
    price: "",
    weight: "",
    quantity: "",
    negotiable: false,
    origin: null,
    tags: [],
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const titleLength = useMemo(
    () => String(productDetails.title ?? "").length,
    [productDetails.title],
  );

  const titleHelperColor = useMemo(() => {
    if (fieldErrors.title) return undefined;
    if (titleLength >= PRODUCT_TITLE_MAX_LENGTH) return "error.main";
    if (titleLength >= PRODUCT_TITLE_MAX_LENGTH * 0.9) {
      return "warning.main";
    }
    return undefined;
  }, [titleLength, fieldErrors.title]);

  const descriptionLength = useMemo(
    () => String(productDetails.description ?? "").length,
    [productDetails.description],
  );

  const descriptionHelperColor = useMemo(() => {
    if (fieldErrors.description) return undefined;
    if (descriptionLength >= PRODUCT_DESCRIPTION_MAX_LENGTH) return "error.main";
    if (descriptionLength >= PRODUCT_DESCRIPTION_MAX_LENGTH * 0.9) {
      return "warning.main";
    }
    return undefined;
  }, [descriptionLength, fieldErrors.description]);

  const { handleClick: notify } = useSnackbarHook();

  const [adminEditProduct, { loading: mutationLoading }] =
    useMutation(ADMIN_EDIT_PRODUCT);

  const originId = productDetails.origin?._id ?? "";
  const { data: statesData, loading: statesLoading } = useQuery(GET_STATES, {
    variables: { country: originId },
    skip: !originId,
  });

  const { data: categories } = useQuery(GET_CATEGORIES_N_SUB);
  const { loading: loadingOrigins, data: origins } =
    useQuery(GET_PRODUCT_ORIGIN);
  const [getproducts, { loading, data, error, refetch }] = useLazyQuery(
    GET_SINGLE_PRODUCTS,
    {
      variables: {
        textToSearch: id,
      },
    },
  );

  useEffect(() => {
    getproducts();
  }, [id]);

  const product = data?.fetchProductsWithFilter?.items;
  const initialProductImages = useMemo(
    () => product?.[0]?.images?.filter(Boolean) || [],
    [product],
  );

  useEffect(() => {
    setImageItems(
      initialProductImages.map((imageUrl, index) => ({
        id: `existing-${index}-${imageUrl}`,
        preview: imageUrl,
        file: null,
        isExisting: true,
      })),
    );
  }, [initialProductImages]);

  useEffect(() => {
    return () => {
      createdImageUrlsRef.current.forEach((previewUrl) => {
        URL.revokeObjectURL(previewUrl);
      });
    };
  }, []);

  const appendImages = (files) => {
    const nextImages = files.map((file) => {
      const preview = URL.createObjectURL(file);
      createdImageUrlsRef.current.push(preview);

      return {
        id: `new-${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        preview,
        file,
        isExisting: false,
      };
    });

    setImageItems((prevImages) => [...prevImages, ...nextImages]);
  };

  const handleRemoveImage = (imageId) => {
    setImageItems((prevImages) => {
      const imageToRemove = prevImages.find((item) => item.id === imageId);

      if (imageToRemove && !imageToRemove.isExisting) {
        URL.revokeObjectURL(imageToRemove.preview);
        createdImageUrlsRef.current = createdImageUrlsRef.current.filter(
          (previewUrl) => previewUrl !== imageToRemove.preview,
        );
      }

      return prevImages.filter((item) => item.id !== imageId);
    });
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: IMAGE_LIMIT_BYTES,
    noKeyboard: true,
    noClick: true,
    onDrop: appendImages,
  });

  // console.log(product[0]);

  useEffect(() => {
    const p = product?.[0];
    if (!p) return;

    setProductDetails((prev) => ({
      ...prev,
      title: p.name ?? "",
      description: p.description ?? "",
      price: p.price != null ? String(p.price) : "",
      weight: p.weight != null ? String(p.weight) : "",
      quantity:
        p.quantityInStock != null ? String(p.quantityInStock) : prev.quantity,
      negotiable: Boolean(p.isNegotiable),
      category: p.categories?.[0] ?? prev.category,
      origin: p.country ?? prev.origin,
      state: p.state ?? prev.state,
      city: p.city ?? prev.city,
      tags:
        p.subCategories?.map((s) => s.name).filter(Boolean) ?? prev.tags ?? [],
    }));
  }, [product]);

  const handleEditProduct = async () => {
    setFieldErrors({});

    const validation = validateEditProductForm({ productDetails, imageItems });
    if (!validation.ok) {
      const next = {};
      validation.errorList.forEach(({ field, message }) => {
        next[field] = message;
      });
      setFieldErrors(next);
      logEditProductDebug("validation_failed", {
        errors: validation.errors,
        imageCount: imageItems.length,
      });
      notify("Please fix the errors before saving.", "warning");
      return;
    }

    setIsSubmitting(true);
    logEditProductDebug("submit_start", {
      productId: id,
      newFileCount: imageItems.filter((i) => i.file instanceof File).length,
    });

    try {
      const uploadedUrls = await uploadNewProductImagesToCloudinary(
        imageItems,
        {
          log: (level, msg, meta) => {
            if (level === "error") {
              console.error("[EditProduct:upload]", msg, meta);
            } else {
              logEditProductDebug("upload", { msg, ...meta });
            }
          },
        },
      );

      const finalImageUrls = mergeProductImageUrlsAfterUpload(
        imageItems,
        uploadedUrls,
      );

      const variables = buildAdminEditProductVariables({
        productId: id,
        productDetails,
        finalImageUrls,
      });

      logEditProductDebug("mutation_variables", {
        ...variables,
        images: `[${variables.images.length} urls]`,
      });

      await adminEditProduct({ variables });

      notify("Product updated successfully.", "success");
      await refetch();
    } catch (err) {
      const gqlMsg = err?.graphQLErrors?.[0]?.message;
      const netMsg = err?.networkError?.message;
      const message =
        gqlMsg || netMsg || err?.message || "Failed to update product";
      console.error("[EditProduct:submit_error]", {
        message,
        graphQLErrors: err?.graphQLErrors,
        networkError: err?.networkError,
        err,
      });
      notify(message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const saving = isSubmitting || mutationLoading;

  console.log(product)

  return (
    <div>
      {loading || saving && <Loader />}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", columnGap: 2 }}>
            <Link to="/dashboard/products">
              <Typography sx={{ fontSize: "13px", color: "#534f4fff" }}>
                Products
              </Typography>
            </Link>
            <Typography>/</Typography>
            <Link to={`/dashboard/product-details/${id}`}>
            <p className="text-[13px] text-black font-medium truncate max-w-[100px]"> {product?.[0]?.name}</p>
           
            </Link>
            <Typography>/</Typography>
            <Typography
              sx={{ fontSize: "13px", color: "#000", fontWeight: 500 }}
            >
              Edit Product
            </Typography>
          </Box>
          <Box>
            <p className="text-[20px] font-bold truncate max-w-[200px] m-0" >    {product?.[0]?.name}</p>
           
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", columnGap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 500,
                py: 0.5,
                px: 1,
                borderRadius: "50px",
                mt: 1,
                color: "#7B408C",
                border: "1px solid #7B408C",
              }}
            >
              {id}
            </Typography>

            <StatusChip status={product?.[0]?.status} outlined={true} />
          </Box>
          <Box sx={{ height: "25px", width: "1px", bgcolor: "#999191ff" }} />

          <Button
            sx={{ borderRadius: "8px", px: 3, bgcolor: "#7B408C" }}
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <FiSave style={{ fontSize: "14px" }} />
              )
            }
            variant="contained"
            disabled={saving || loading}
            onClick={handleEditProduct}
          >
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        {Object.keys(fieldErrors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFieldErrors({})}>
            <Typography sx={{ fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
              Fix the following before saving:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {Object.entries(fieldErrors).map(([key, msg]) => (
                <li key={key}>
                  <Typography sx={{ fontSize: "12px" }}>{msg}</Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item lg={5} md={6} xs={12}>
            <Box sx={{ boxShadow: 1, bgcolor: "#fff", borderRadius: "16px" }}>
              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  alignItems: "center",
                  columnGap: 2,
                  px: 2,
                }}
              >
                <IoImagesOutline />
                <Typography>Product Images</Typography>
              </Box>
              <Divider />
              <Box sx={{ px: 3, py: 4 }}>
                <Box
                  {...getRootProps()}
                  onClick={open}
                  sx={{
                    py: 3,
                    px: 2,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px dashed #ccc",
                    borderRadius: "16px",
                    cursor: "pointer",
                    bgcolor: "#fcfbfd",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#7B408C",
                      bgcolor: "#f8f4fa",
                    },
                  }}
                >
                  <input {...getInputProps()} />
                  <Box
                    sx={{
                      display: "grid",
                      placeContent: "center",
                      width: "50px",
                      height: "50px",
                      borderRadius: "50%",
                      bgcolor: "#F5EEF7",
                    }}
                  >
                    <GoUpload style={{ color: "#2E1834" }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, mt: 1 }}>
                    Drop Images Here
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#666" }}>
                    Or click to upload (Max 5MB each)
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Grid container spacing={2}>
                    {imageItems.map((img) => (
                      <Grid item lg={4} md={4} xs={6} key={img.id}>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: "12px",
                            overflow: "hidden",
                            border: "1px solid #ddd",
                            height: "100px",
                            "&:hover .image-delete-overlay": {
                              opacity: 1,
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src={img.preview}
                            alt={product?.[0]?.name || "Product image"}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <Box
                            className="image-delete-overlay"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              bgcolor: "rgba(18, 18, 18, 0.45)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              opacity: 0,
                              transition: "opacity 0.2s ease",
                            }}
                          >
                            <IconButton
                              onClick={() => handleRemoveImage(img.id)}
                              sx={{
                                bgcolor: "#fff",
                                color: "#de3b40",
                                "&:hover": {
                                  bgcolor: "#fff5f5",
                                },
                              }}
                            >
                              <FiTrash2 size={16} />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                    <Grid item lg={4} md={4} xs={6}>
                      <Box
                        onClick={open}
                        sx={{
                          border: "1px dashed #ccc",
                          display: "grid",
                          placeContent: "center",
                          height: "100px",
                          borderRadius: "12px",
                          cursor: "pointer",
                          bgcolor: "#fcfbfd",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            borderColor: "#7B408C",
                            bgcolor: "#f8f4fa",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "grid",
                            placeItems: "center",
                            rowGap: 0.5,
                          }}
                        >
                          <IoAddOutline style={{ fontSize: "30px" }} />
                          <Typography
                            sx={{ fontSize: "11px", fontWeight: 600 }}
                          >
                            Add More
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Divider />
              <Box sx={{ px: 2, py: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: "12px" }}>
                  SUPPORTED: JPG, PNG, WEBP
                </Typography>
                {fieldErrors.images && (
                  <Typography sx={{ color: "error.main", fontSize: "11px", mt: 1 }}>
                    {fieldErrors.images}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item lg={7} md={6}>
            <Box>
              <Box
                sx={{
                  boxShadow: 1,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  px: 3,
                  py: 2,
                }}
              >
                <div className="flex items-center gap-x-2">
                  <IoIosInformationCircleOutline />
                  <Typography sx={{ fontWeight: 700 }}>
                    Basic Information
                  </Typography>
                </div>

                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item md={12}>
                      <Box>
                        <CustomInput
                          placeholder="Enter product title"
                          label={"Product Title"}
                          required
                          value={productDetails?.title}
                          inputProps={{
                            maxLength: PRODUCT_TITLE_MAX_LENGTH,
                          }}
                          error={Boolean(fieldErrors.title)}
                          helperText={
                            fieldErrors.title ||
                            `${titleLength} / ${PRODUCT_TITLE_MAX_LENGTH} characters`
                          }
                          FormHelperTextProps={{
                            style: { fontSize: "10px" },
                            sx: titleHelperColor
                              ? { color: titleHelperColor }
                              : undefined,
                          }}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              title: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item md={6}>
                      <Box>
                        <MaSearchableSelect
                          label="Category"
                          options={
                            categories?.fetchAllCategoriesAndTheirSubCategories?.map(
                              (cat) => ({
                                label: cat?.category?.name ?? "",
                                value: cat?.category?._id ?? cat?.category,
                              }),
                            ) ?? []
                          }
                          value={productDetails.category?._id ?? ""}
                          onChange={(selectedId) => {
                            const cat =
                              categories?.fetchAllCategoriesAndTheirSubCategories?.find(
                                (c) =>
                                  (c?.category?._id ?? c?.category) ===
                                  selectedId,
                              );
                            setProductDetails({
                              ...productDetails,
                              category:
                                cat?.category ?? null,
                            });
                          }}
                          placeholder="Select category"
                        />
                        {fieldErrors.category && (
                          <Typography sx={{ color: "error.main", fontSize: "11px", mt: 0.5 }}>
                            {fieldErrors.category}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item md={6}>
                      <Box>
                        <MaSearchableSelect
                          label="Product Origin"
                          options={
                            origins?.getProductOrigins?.items?.map((item) => ({
                              label: item?.name ?? "",
                              value: item?._id ?? item,
                            })) ?? []
                          }
                          value={productDetails?.origin?._id ?? ""}
                          onChange={(selectedId) => {
                            const origin =
                              origins?.getProductOrigins?.items?.find(
                                (o) => (o?._id ?? o) === selectedId,
                              );
                            setProductDetails({
                              ...productDetails,
                              origin: origin ?? null,
                              state: null,
                            });
                          }}
                          placeholder={
                            loadingOrigins ? "Loading..." : "Select country"
                          }
                        />
                        {fieldErrors.origin && (
                          <Typography sx={{ color: "error.main", fontSize: "11px", mt: 0.5 }}>
                            {fieldErrors.origin}
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item md={6}>
                      <Box>
                        <MaSearchableSelect
                          label="State / region (optional)"
                          disabled={!originId || statesLoading}
                          options={
                            statesData?.fetchStates?.items?.map((s) => ({
                              label: s?.name ?? "",
                              value: s?._id ?? s,
                            })) ?? []
                          }
                          value={productDetails.state?._id ?? ""}
                          onChange={(selectedId) => {
                            const st =
                              statesData?.fetchStates?.items?.find(
                                (s) => (s?._id ?? s) === selectedId,
                              );
                            setProductDetails({
                              ...productDetails,
                              state: st ?? null,
                            });
                          }}
                          placeholder={
                            !originId
                              ? "Select origin first"
                              : statesLoading
                                ? "Loading states…"
                                : "Select state (optional)"
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item md={6}>
                      <Box>
                        <CustomInput
                          placeholder="e.g. 1.5 (optional)"
                          label={"Weight (kg) (optional)"}
                          type="text"
                          inputMode="decimal"
                          value={productDetails?.weight}
                          error={Boolean(fieldErrors.weight)}
                          helperText={fieldErrors.weight || " "}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              weight: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Grid>

                    <Grid item md={12}>
                      <div>
                        <CustomInput
                          value={productDetails?.description}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              description: e.target.value,
                            })
                          }
                          label="Full Description"
                          required
                          multiline
                          minRows={4}
                          inputProps={{
                            maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH,
                          }}
                          error={Boolean(fieldErrors.description)}
                          helperText={
                            fieldErrors.description ||
                            `${descriptionLength} / ${PRODUCT_DESCRIPTION_MAX_LENGTH} characters`
                          }
                          FormHelperTextProps={{
                            style: { fontSize: "10px" },
                            sx: descriptionHelperColor
                              ? { color: descriptionHelperColor }
                              : undefined,
                          }}
                        />
                      </div>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            {/* PRICING & STOCKS */}
            <Box>
              <Box
                sx={{
                  boxShadow: 1,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  px: 3,
                  py: 2,
                  mt: 2,
                }}
              >
                <div className="flex items-center gap-x-2">
                  <IoPricetagsOutline />
                  <Typography sx={{ fontWeight: 700 }}>
                    Pricing & Stock
                  </Typography>
                </div>

                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item md={6}>
                      <Box>
                        <CustomInput
                          placeholder=""
                          startIcon={"$"}
                          label={"Price (USD)"}
                          required
                          type="text"
                          inputMode="decimal"
                          value={productDetails?.price}
                          error={Boolean(fieldErrors.price)}
                          helperText={fieldErrors.price || " "}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              price: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item md={6}>
                      <Box>
                        <CustomInput
                          placeholder=""
                          label={"Current Stock"}
                          value={productDetails?.quantity}
                          onChange={(e) =>
                            setProductDetails({
                              ...productDetails,
                              quantity: e.target.value,
                            })
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item md={12}>
                      <div className="bg-main100 p-3 rounded-[16px] border border-solid border-[#D7B9DF] flex items-center justify-between">
                        <div className="flex items-center gap-x-2">
                          <div className="grid place-content-center w-[40px] h-[40px] rounded-full bg-[#D7B9DF]">
                            <IoIosCheckmarkCircleOutline className="text-primary" />
                          </div>
                          <div>
                            <p className="font-[700] m-0">Price Negotiation</p>
                            <span className="text-[12px]">
                              Allow buyers to negotiate price
                            </span>
                          </div>
                        </div>
                        <div>
                          <Checkbox
                          checked={productDetails?.negotiable}
                          onChange={(e)=>setProductDetails({...productDetails, negotiable:e.target.checked})}
                            sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
                          />
                        </div>
                      </div>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>

            {/* OPTIONS */}
            <Box sx={{mb:10}}>
              <Box
                sx={{
                  boxShadow: 1,
                  bgcolor: "#fff",
                  borderRadius: "16px",
                  px: 3,
                  py: 2,
                  mt: 2,
                }}
              >
                <div className="flex items-center gap-x-2">
                  <CgOptions />
                  <Typography sx={{ fontWeight: 700 }}>Options</Typography>
                </div>

                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item md={12}>
                      <Box>
                        <div className="mb-2 flex flex-col ">
                          <span className="pb-1 text-[13px] font-[600]">
                            Product Tags
                          </span>
                          <span className="text-[12px]">
                            Enter keywords buyers might use to search for this
                            item
                          </span>
                        </div>
                        <TagInput
                          value={productDetails?.tags}
                          onChange={(tagNames) => {
                            setProductDetails({
                              ...productDetails,
                              tags: tagNames,
                            });
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default EditProduct;
